import type { CustomInfoType } from "$lib/models/base";
import { googleInfoTypes } from "./googleInfoTypes";

/**
 * Detectors we surface as a category of their own instead of folding them into Google's parent.
 *
 * Australia and the United States are the markets we serve first, and an Australian customer
 * looking for tax file numbers should not have to open "Government ID" to find them. Every entry
 * here is a market decision; nothing else belongs in this set.
 */
const OWN_CATEGORY = new Set([
  "AUSTRALIA_DRIVERS_LICENSE_NUMBER",
  "AUSTRALIA_MEDICARE_NUMBER",
  "AUSTRALIA_PASSPORT",
  "AUSTRALIA_TAX_FILE_NUMBER",
  "STORAGE_SIGNED_POLICY_DOCUMENT",
  "US_ADOPTION_TAXPAYER_IDENTIFICATION_NUMBER",
  "US_DEA_NUMBER",
  "US_DRIVERS_LICENSE_NUMBER",
  "US_HEALTHCARE_NPI",
  "US_INDIVIDUAL_TAXPAYER_IDENTIFICATION_NUMBER",
  "US_MEDICARE_BENEFICIARY_ID_NUMBER",
  "US_PASSPORT",
  "US_PREPARER_TAXPAYER_IDENTIFICATION_NUMBER",
  "US_SOCIAL_SECURITY_NUMBER",
]);

/**
 * Detectors Google publishes no parent for, which we group anyway so they do not each become a
 * category holding one thing.
 */
const EXTRA_PARENT: Record<string, string> = {
  MEDICAL_ID: "MEDICAL_DATA",
  MEDICAL_RECORD_NUMBER: "MEDICAL_DATA",
  US_EMPLOYER_IDENTIFICATION_NUMBER: "GOVERNMENT_ID",
  VAT_NUMBER: "GOVERNMENT_ID",
};

function categoryOf(name: string): string {
  if (OWN_CATEGORY.has(name)) return name;
  return EXTRA_PARENT[name] ?? googleInfoTypes[name]?.parent ?? name;
}

export const infoTypes: { [key: string]: string } = Object.fromEntries(
  Object.keys(googleInfoTypes).map((name) => [name, categoryOf(name)])
);

/**
 * Get combined infoTypes including custom infoTypes from the organization.
 * Custom infoTypes map to themselves (they are their own category).
 */
export function getInfoTypesWithCustom(
  customInfoTypes: CustomInfoType[]
): Record<string, string> {
  const combined = { ...infoTypes };

  for (const customType of customInfoTypes) {
    if (customType.enabled && customType.name) {
      combined[customType.name] = customType.name;
    }
  }

  return combined;
}
